import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { EmptyState, ErrorBanner, Spinner } from './components/ui.js';
import { appMetaQuery, sessionQuery } from './lib/meta.js';
import { errorMessage, queryClient } from './lib/trpc.js';
import { Dashboard } from './screens/dashboard.js';
import { DashboardEmpty, OwnerDashboard } from './screens/owner-dashboard.js';
import { EntityList, validateListSearch } from './screens/entity-list.js';
import { ExtensionRoute } from './screens/extension-route.js';
import { WhatsNewScreen } from './help/whats-new.js';
import { ClientHelpGuide, HelpGuide, HelpIndex } from './screens/help.js';
import { AcceptInviteScreen, LoginScreen } from './screens/login.js';
import { RecordDetail } from './screens/record-detail.js';
import { RecordNew, validateNewSearch } from './screens/record-new.js';
import { ReportIndex, ReportScreen } from './screens/reports.js';
import { SettingsScreen } from './screens/settings.js';
import { Shell } from './screens/shell.js';
import { TurnOffSummaryScreen } from './screens/turn-off-summary.js';
import { ApproveScreen } from './screens/approve.js';
import { ViewerScreen } from './screens/viewer.js';

/**
 * The app's screens. Every entity uses the same three (list, new, record), drawn from
 * metadata, so a client's custom entity has screens the moment it is declared. Client
 * screens mount under /x through the `app.routes` slot.
 */

interface RouterContext {
  queryClient: QueryClient;
}

function RouteError({ error }: ErrorComponentProps) {
  return (
    <ErrorBanner title="Something went wrong on this screen">{errorMessage(error)}</ErrorBanner>
  );
}

function NotFound() {
  return (
    <EmptyState title="There is nothing here">
      The link may be out of date. Use the menu to find your way.
    </EmptyState>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { next?: string } =>
    typeof search['next'] === 'string' ? { next: search['next'] } : {},
  component: LoginScreen,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search['token'] === 'string' ? { token: search['token'] } : {},
  component: AcceptInviteScreen,
});

/** A dashboard opened by a read-only view link, with no sign-in. */
const viewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/view',
  component: ViewerScreen,
});

/** Turning off the dashboard's summary email from the link in it, with no sign-in. */
const turnOffSummaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unsubscribe',
  component: TurnOffSummaryScreen,
});

/** Answering an approval request from the buttons in its email, with no sign-in. */
const approveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approve',
  component: ApproveScreen,
});

/** Everything behind sign-in. Loads the session and the deploy's metadata before any screen draws. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session)
      throw redirect({
        to: '/login',
        search: location.href === '/' ? {} : { next: location.href },
      });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(appMetaQuery),
  component: Shell,
});

/** Home opens on the owner dashboard once the organization has data for it. */
const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    const meta = await context.queryClient.ensureQueryData(appMetaQuery);
    const first = meta.dashboard.views[0];
    if (meta.dashboard.has_data && first)
      throw redirect({ to: '/dashboard/$view', params: { view: first.key }, replace: true });
  },
  component: Dashboard,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  beforeLoad: async ({ context }) => {
    const meta = await context.queryClient.ensureQueryData(appMetaQuery);
    const first = meta.dashboard.views[0];
    if (meta.dashboard.has_data && first)
      throw redirect({ to: '/dashboard/$view', params: { view: first.key }, replace: true });
  },
  component: DashboardEmpty,
});

const dashboardViewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard/$view',
  component: OwnerDashboard,
});

const listRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/e/$entity',
  validateSearch: validateListSearch,
  component: EntityList,
});

const newRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/e/$entity/new',
  validateSearch: validateNewSearch,
  component: RecordNew,
});

const recordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/e/$entity/$id',
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search['tab'] === 'string' && /^[a-z0-9_.-]+$/.test(search['tab'])
      ? { tab: search['tab'] }
      : {},
  component: RecordDetail,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportIndex,
});

const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/$key',
  component: ReportScreen,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsScreen,
});

/** Help: the core's guide to each screen, then the client's own pages (`app.help`). */
const helpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help',
  component: HelpIndex,
});

const helpGuideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help/$guide',
  component: HelpGuide,
});

const clientHelpGuideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help/x/$guide',
  component: ClientHelpGuide,
});

/** What's new: the release notes that apply to this deploy. */
const whatsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/whats-new',
  component: WhatsNewScreen,
});

const extensionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/x/$',
  component: ExtensionRoute,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  acceptInviteRoute,
  viewerRoute,
  turnOffSummaryRoute,
  approveRoute,
  appRoute.addChildren([
    homeRoute,
    dashboardRoute,
    dashboardViewRoute,
    listRoute,
    newRoute,
    recordRoute,
    reportsRoute,
    reportRoute,
    settingsRoute,
    helpRoute,
    helpGuideRoute,
    clientHelpGuideRoute,
    whatsNewRoute,
    extensionRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Queries own caching; the router only decides when to ask.
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => <Spinner />,
  defaultErrorComponent: RouteError,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
